// B3 commit 4: chat card for the guard-checked deal touch strategy
// (`crm_touch_strategy_v1`). Rendering follows the SalesHealthSnippet
// conventions (CSS variables, i18n subscription, data-testids); the approval
// footer follows the AiApprovalCard decision flow (useApprovalMutation →
// onDecisionApplied). The strategy text itself stays read-only: creating the
// tasks is the separate approval-gated create_deal_tasks execution — there is
// no undo for it, so resolved approvals render as terminal status lines.
import { useTranslation } from '@govard/i18n-core/react';
import { useI18nSubscription } from '../../../i18n/useI18nSubscription.js';
import { i18n } from '../../../../i18n/index.js';
import { useState, type ReactElement, type ReactNode } from 'react';
import { CalendarDays, Mail, Phone, Send } from 'lucide-react';

import { Badge, Button } from '@govard/ui-primitives';

import { useApprovalMutation } from '../../hooks/useApprovalMutation.js';
import type { ApprovalItem, ApprovalStatus } from '../../../approvals/api/approvals.client.js';
import { formatDateTime } from '../../../../shared/format/index.js';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

export type TouchStrategyPayload = Extract<
  StructuredMessagePayload,
  { type: 'crm_touch_strategy_v1' }
>;

/** Compact failure codes from the propose_touch_strategy tool envelope. */
export type TouchStrategyFailureCode =
  | 'touch_strategy_guard'
  | 'touch_strategy_llm'
  | 'touch_strategy_persist';

const FAILURE_LABEL_KEYS: Readonly<Record<TouchStrategyFailureCode, string>> = {
  touch_strategy_guard: 'crm.chat.touch_strategy.status_guard',
  touch_strategy_llm: 'crm.chat.touch_strategy.status_llm',
  touch_strategy_persist: 'crm.chat.touch_strategy.status_persist',
};

const DRAFT_PREVIEW_LIMIT = 120;

const APPROVAL_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'approved',
  'rejected',
  'expired',
  'cancelled',
]);

const CHANNEL_ICONS: Readonly<Record<TouchStrategyPayload['steps'][number]['channel'], ReactNode>> =
  {
    email: <Mail size={11} aria-hidden="true" />,
    call: <Phone size={11} aria-hidden="true" />,
    meeting: <CalendarDays size={11} aria-hidden="true" />,
    telegram: <Send size={11} aria-hidden="true" />,
  };

type TouchStrategySnippetProps =
  | {
      strategy: TouchStrategyPayload;
      onDecisionApplied?: ((approval: ApprovalItem) => void) | undefined;
    }
  | { failureCode: TouchStrategyFailureCode };

interface ResolvedDecision {
  readonly status: ApprovalStatus;
  readonly executionStatus: string | undefined;
  readonly decidedAt: string | undefined;
}

type FooterState =
  | { readonly kind: 'read_only' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'executed'; readonly decidedAt: string | undefined }
  | { readonly kind: 'verifying' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'declined' };

function approvalStatusOf(value: string | undefined): ApprovalStatus {
  return value !== undefined && APPROVAL_STATUSES.has(value)
    ? (value as ApprovalStatus)
    : 'pending';
}

function footerStateOf(
  strategy: TouchStrategyPayload,
  resolved: ResolvedDecision | null,
): FooterState {
  if (strategy.approvalId === undefined) return { kind: 'read_only' };
  const status = resolved?.status ?? approvalStatusOf(strategy.approvalStatus);
  if (status === 'rejected' || status === 'expired' || status === 'cancelled') {
    return { kind: 'declined' };
  }
  if (status === 'approved') {
    const executionStatus = resolved?.executionStatus ?? strategy.executionStatus;
    if (executionStatus === 'succeeded') {
      return { kind: 'executed', decidedAt: resolved?.decidedAt ?? strategy.decidedAt };
    }
    if (executionStatus === 'failed') return { kind: 'failed' };
    return { kind: 'verifying' };
  }
  return { kind: 'pending' };
}

function localizedEvidence(code: string): string {
  return i18n.t(`crm.chat.sales_health.reason_${code}`) as string;
}

function StrategyStep({
  step,
  index,
}: {
  step: TouchStrategyPayload['steps'][number];
  index: number;
}): ReactElement {
  const { t } = useTranslation();
  const [draftExpanded, setDraftExpanded] = useState(false);
  const draft = step.draft ?? null;
  const previewDraft = draft !== null && draft.length > DRAFT_PREVIEW_LIMIT;
  const visibleDraft =
    draft === null
      ? null
      : previewDraft && !draftExpanded
        ? `${draft.slice(0, DRAFT_PREVIEW_LIMIT)}…`
        : draft;

  return (
    <li
      data-testid={`touch-strategy-step-${index}`}
      className="rounded-md border border-[var(--ant-color-border)] p-2.5"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ant-color-border)] bg-[var(--ant-color-bg-container)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--ant-color-text)]">
          {CHANNEL_ICONS[step.channel]}
          {t(`crm.chat.touch_strategy.channel_${step.channel}`)}
        </span>
        <span className="rounded-full bg-[var(--ant-color-primary-bg)] px-2 py-0.5 font-mono text-[11px] font-semibold text-[var(--ant-color-primary-text)]">
          {t('crm.chat.touch_strategy.day_offset', { n: step.dayOffset })}
        </span>
      </div>
      <p className="mt-1.5 text-xs text-[var(--ant-color-text)]">{step.goal}</p>
      {visibleDraft !== null ? (
        <div className="mt-1.5 rounded-md bg-[var(--color-surface)] px-2.5 py-2">
          <p
            data-testid={`touch-strategy-draft-${index}`}
            className="whitespace-pre-wrap text-[11px] leading-relaxed text-[var(--ant-color-text-secondary)]"
          >
            {visibleDraft}
          </p>
          {previewDraft ? (
            <button
              type="button"
              data-testid={`touch-strategy-draft-toggle-${index}`}
              className="mt-1 cursor-pointer border-none bg-transparent p-0 text-[11px] font-medium text-[var(--ant-color-primary)] hover:underline"
              onClick={() => setDraftExpanded((current) => !current)}
            >
              {draftExpanded
                ? t('crm.chat.touch_strategy.draft_preview_hide')
                : t('crm.chat.touch_strategy.draft_preview_show')}
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function FailureNotice({ failureCode }: { failureCode: TouchStrategyFailureCode }): ReactElement {
  useI18nSubscription();
  const { t } = useTranslation();
  return (
    <div
      data-testid="structured-snippet-touch-strategy"
      className="my-2 rounded-xl border border-[var(--ant-color-error-border)] bg-[var(--ant-color-error-bg)] px-3.5 py-2.5"
    >
      <p className="text-xs text-[var(--ant-color-error-text)]">
        {t(FAILURE_LABEL_KEYS[failureCode])}
      </p>
    </div>
  );
}

export function TouchStrategySnippet(props: TouchStrategySnippetProps): ReactElement {
  useI18nSubscription();
  const { t, tc } = useTranslation();
  const approvalMutation = useApprovalMutation();
  const [resolved, setResolved] = useState<ResolvedDecision | null>(null);

  if ('failureCode' in props) {
    return <FailureNotice failureCode={props.failureCode} />;
  }
  const { strategy, onDecisionApplied } = props;

  const footer = footerStateOf(strategy, resolved);

  const handleDecision = async (decision: 'approve' | 'reject'): Promise<void> => {
    if (strategy.approvalId === undefined) return;
    try {
      const updatedApproval = await approvalMutation.mutateAsync({
        approvalId: strategy.approvalId,
        decision,
      });
      setResolved({
        status: updatedApproval.status,
        executionStatus: updatedApproval.executionOutcome?.status,
        decidedAt: updatedApproval.decidedAt ?? undefined,
      });
      onDecisionApplied?.(updatedApproval);
    } catch {
      // reported by useApprovalMutation onError
    }
  };

  return (
    <div
      data-testid="structured-snippet-touch-strategy"
      className="my-2 overflow-hidden rounded-xl border border-[var(--ant-color-border)] bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--ant-color-border)] px-3.5 py-2.5">
        <span className="text-xs font-semibold text-[var(--ant-color-text-heading)]">
          {t('crm.chat.touch_strategy.title')}
        </span>
        <span className="flex items-center gap-1.5">
          <Badge variant="outline">
            {t(`crm.chat.touch_strategy.preset_${strategy.sourcePreset}`)}
          </Badge>
          {strategy.approvalId === undefined ? (
            <Badge
              variant="outline"
              className="text-[10px] font-medium uppercase tracking-wide text-[var(--ant-color-text-secondary)]"
            >
              {t('crm.chat.touch_strategy.read_only')}
            </Badge>
          ) : null}
        </span>
      </div>

      <div className="space-y-3.5 p-3.5">
        {strategy.historyAdvancedSince === true ? (
          <p
            data-testid="touch-strategy-stale-warning"
            className="rounded-md border border-[var(--ant-color-warning-border)] bg-[var(--ant-color-warning-bg)] px-3 py-2 text-xs text-[var(--ant-color-warning-text)]"
          >
            {t('crm.chat.touch_strategy.history_advanced')}
          </p>
        ) : null}

        <section data-testid="touch-strategy-diagnosis">
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--ant-color-text-secondary)]">
            {t('crm.chat.touch_strategy.diagnosis_label')}
          </p>
          <p className="text-xs leading-relaxed text-[var(--ant-color-text)]">
            {strategy.diagnosis}
          </p>
        </section>

        <section data-testid="touch-strategy-steps">
          <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--ant-color-text-secondary)]">
            {t('crm.chat.touch_strategy.steps_title')}
          </p>
          <ul className="space-y-2">
            {strategy.steps.map((step, index) => (
              <StrategyStep key={`${step.dayOffset}-${index}`} step={step} index={index} />
            ))}
          </ul>
        </section>

        {strategy.evidenceSignalCodes.length > 0 ? (
          <section data-testid="touch-strategy-evidence">
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--ant-color-text-secondary)]">
              {t('crm.chat.touch_strategy.evidence')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {strategy.evidenceSignalCodes.map((code) => (
                <span
                  key={code}
                  className="rounded-full border border-[var(--ant-color-warning-border)] bg-[var(--ant-color-warning-bg)] px-2 py-0.5 text-[10px] font-medium text-[var(--ant-color-warning-text)]"
                >
                  {localizedEvidence(code)}
                </span>
              ))}
            </div>
          </section>
        ) : null}

        {footer.kind === 'pending' && strategy.approvalId !== undefined ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-[var(--ant-color-border)] pt-3">
            <Button
              type="button"
              variant="primary"
              size="sm"
              data-testid="touch-strategy-create-tasks"
              disabled={approvalMutation.isPending}
              onClick={() => {
                void handleDecision('approve');
              }}
            >
              {tc('crm.chat.touch_strategy.create_tasks', strategy.steps.length)}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="touch-strategy-decline"
              disabled={approvalMutation.isPending}
              onClick={() => {
                void handleDecision('reject');
              }}
            >
              {t('crm.chat.touch_strategy.decline')}
            </Button>
          </div>
        ) : null}

        {footer.kind === 'executed' ? (
          <p
            data-testid="touch-strategy-status-executed"
            className="rounded-md border border-[var(--ant-color-success-border)] bg-[var(--ant-color-success-bg)] px-3 py-2 text-xs text-[var(--ant-color-success-text)]"
          >
            {t('crm.chat.touch_strategy.status_executed')}
            {footer.decidedAt !== undefined
              ? ` · ${t('crm.chat.touch_strategy.executed_at', { time: formatDateTime(footer.decidedAt) })}`
              : ''}
          </p>
        ) : null}

        {footer.kind === 'verifying' ? (
          <p
            data-testid="touch-strategy-status-pending"
            className="rounded-md bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--ant-color-text-secondary)]"
          >
            {t('crm.chat.touch_strategy.status_pending')}
          </p>
        ) : null}

        {footer.kind === 'failed' ? (
          <p
            data-testid="touch-strategy-status-failed"
            className="rounded-md border border-[var(--ant-color-error-border)] bg-[var(--ant-color-error-bg)] px-3 py-2 text-xs text-[var(--ant-color-error-text)]"
          >
            {t('crm.approvals.execution_failed')}
          </p>
        ) : null}

        {footer.kind === 'declined' ? (
          <p
            data-testid="touch-strategy-status-declined"
            className="rounded-md bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--ant-color-text-secondary)]"
          >
            {t('crm.chat.touch_strategy.status_declined')}
          </p>
        ) : null}
      </div>
    </div>
  );
}
